// frontend/src/features/portfolio-diversification-optimizer-pro/components/ConcentrationAttributionPanel.tsx
//
// Pillar 2 -- Concentration & Euler Risk Attribution. Renders the HHI / Gini
// summary strip, a Recharts Lorenz capital-vs-risk inequality curve, the PCA
// eigen-spectrum ComposedChart, and an institutional TanStack Table v8 of the
// per-asset Euler volatility + 95% CVaR attribution with ticker search, HRP
// cluster pills, a risk-regime filter, and multi-column sorting.
//
// Themed for the shared light/dark dashboard shell.

import React, { useMemo, useState } from "react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  type SortingState,
  useReactTable,
} from "@tanstack/react-table";
import {
  Area,
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  BarChart3,
  Filter,
  PieChart,
  Scale,
  Search,
} from "lucide-react";

import type { AssetRiskAttribution, ConcentrationAnalysis } from "../types";

interface ConcentrationAttributionPanelProps {
  concentration: ConcentrationAnalysis;
}

const columnHelper = createColumnHelper<AssetRiskAttribution>();

const CHART_GRID = "#e2e8f0";
const CHART_AXIS = "#94a3b8";
const TOOLTIP_STYLE: React.CSSProperties = {
  borderRadius: 12,
  border: "1px solid #e2e8f0",
  fontSize: 12,
  background: "rgba(255,255,255,0.97)",
  color: "#0f172a",
};

const summaryCardClass =
  "rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900/90";
const chartCardClass =
  "rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900/90";

export const ConcentrationAttributionPanel: React.FC<ConcentrationAttributionPanelProps> = ({
  concentration,
}) => {
  // TanStack Table state
  const [sorting, setSorting] = useState<SortingState>([{ id: "pctr", desc: true }]);
  const [searchFilter, setSearchFilter] = useState("");
  const [selectedCluster, setSelectedCluster] = useState<number | "all">("all");
  const [riskFilter, setRiskFilter] = useState<"all" | "overweight" | "hedge">("all");

  // Unique cluster IDs for the filter pills.
  const clusterIds = useMemo(() => {
    const set = new Set<number>(concentration.asset_attribution.map((r) => r.cluster));
    return Array.from(set).sort((a, b) => a - b);
  }, [concentration.asset_attribution]);

  // Pre-filter by search, cluster pill, and risk regime before TanStack.
  const filteredRows = useMemo(() => {
    return concentration.asset_attribution.filter((row) => {
      if (
        searchFilter.trim() &&
        !row.ticker.toLowerCase().includes(searchFilter.trim().toLowerCase())
      ) {
        return false;
      }
      if (selectedCluster !== "all" && row.cluster !== selectedCluster) {
        return false;
      }
      if (riskFilter === "overweight" && row.risk_vs_capital_gap <= 0.02) {
        return false;
      }
      if (riskFilter === "hedge" && row.risk_vs_capital_gap >= -0.01) {
        return false;
      }
      return true;
    });
  }, [concentration.asset_attribution, searchFilter, selectedCluster, riskFilter]);

  const columns = useMemo(
    () => [
      columnHelper.accessor("ticker", {
        header: "Ticker",
        cell: (info) => (
          <div className="flex items-center gap-2">
            <span className="font-mono font-bold text-slate-900 dark:text-white">
              {info.getValue()}
            </span>
            <span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[10px] text-slate-500 dark:bg-slate-800 dark:text-slate-400">
              C{info.row.original.cluster}
            </span>
          </div>
        ),
      }),
      columnHelper.accessor("weight", {
        header: "Capital Weight",
        cell: (info) => (
          <span className="font-mono text-slate-700 dark:text-slate-200">
            {(info.getValue() * 100).toFixed(2)}%
          </span>
        ),
      }),
      columnHelper.accessor("pctr", {
        header: "Vol Risk Share (PCTR)",
        cell: (info) => {
          const pctr = info.getValue();
          const weight = info.row.original.weight;
          const isOverweight = pctr > weight + 0.03;
          return (
            <div className="flex items-center gap-2.5">
              <div className="h-2 w-20 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
                <div
                  className={`h-full rounded-full ${
                    pctr < 0 ? "bg-emerald-400" : isOverweight ? "bg-amber-500" : "bg-indigo-500"
                  }`}
                  style={{ width: `${Math.min(100, Math.max(0, Math.abs(pctr) * 100))}%` }}
                />
              </div>
              <span
                className={`font-mono font-semibold ${
                  pctr < 0
                    ? "text-emerald-600 dark:text-emerald-400"
                    : isOverweight
                    ? "text-amber-600 dark:text-amber-300"
                    : "text-slate-900 dark:text-white"
                }`}
              >
                {(pctr * 100).toFixed(2)}%
              </span>
            </div>
          );
        },
      }),
      columnHelper.accessor("cvar_95_pctr", {
        header: "95% Tail CVaR Share",
        cell: (info) => {
          const val = info.getValue();
          return (
            <span
              className={`font-mono ${
                val > info.row.original.weight + 0.04
                  ? "font-semibold text-rose-600 dark:text-rose-400"
                  : "text-slate-600 dark:text-slate-300"
              }`}
            >
              {(val * 100).toFixed(2)}%
            </span>
          );
        },
      }),
      columnHelper.accessor("risk_vs_capital_gap", {
        header: "Risk vs. Capital Gap",
        cell: (info) => {
          const gap = info.getValue();
          const formatted = `${gap >= 0 ? "+" : ""}${(gap * 100).toFixed(2)}%`;
          return (
            <span
              className={`inline-flex rounded px-2 py-0.5 font-mono text-xs font-semibold ${
                gap > 0.05
                  ? "bg-rose-500/15 text-rose-700 dark:text-rose-300"
                  : gap > 0.01
                  ? "bg-amber-500/15 text-amber-700 dark:text-amber-300"
                  : gap < -0.02
                  ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
                  : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300"
              }`}
            >
              {formatted}
            </span>
          );
        },
      }),
      columnHelper.accessor("mctr", {
        header: "Marginal Risk (MCTR)",
        cell: (info) => (
          <span className="font-mono text-slate-600 dark:text-slate-300">
            {(info.getValue() * 100).toFixed(2)}%
          </span>
        ),
      }),
      columnHelper.accessor("annualized_volatility", {
        header: "Standalone Vol",
        cell: (info) => (
          <span className="font-mono text-slate-600 dark:text-slate-300">
            {(info.getValue() * 100).toFixed(2)}%
          </span>
        ),
      }),
      columnHelper.accessor("diversification_benefit", {
        header: "Div Benefit",
        cell: (info) => (
          <span className="font-mono text-emerald-600 dark:text-emerald-400">
            -{(info.getValue() * 100).toFixed(2)}%
          </span>
        ),
      }),
    ],
    []
  );

  const table = useReactTable({
    data: filteredRows,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  // Lorenz curve data -> percentages for Recharts.
  const lorenzChartData = useMemo(
    () =>
      concentration.lorenz_curve.map((pt) => ({
        holdingsPct: Number((pt.cumulative_holdings_pct * 100).toFixed(1)),
        equalityPct: Number((pt.cumulative_holdings_pct * 100).toFixed(1)),
        capitalPct: Number((pt.cumulative_capital_pct * 100).toFixed(1)),
        riskPct: Number((pt.cumulative_risk_pct * 100).toFixed(1)),
      })),
    [concentration.lorenz_curve]
  );

  // PCA data -> percentages for Recharts.
  const pcaChartData = useMemo(
    () =>
      concentration.pca.map((pc) => ({
        component: pc.component,
        marketVariancePct: Number((pc.variance_explained * 100).toFixed(1)),
        portfolioSharePct: Number((pc.portfolio_variance_share * 100).toFixed(1)),
        cumulativePct: Number((pc.cumulative_variance * 100).toFixed(1)),
      })),
    [concentration.pca]
  );

  return (
    <div className="space-y-6">
      {/* Concentration & HHI Summary Strip */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
        <div className={summaryCardClass}>
          <div className="text-[11px] text-slate-500 dark:text-slate-400">Capital HHI</div>
          <div className="mt-1 font-mono text-xl font-bold text-slate-900 dark:text-white">
            {concentration.hhi_weight.toFixed(3)}
          </div>
          <div className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
            Eff. N:{" "}
            <strong className="font-mono text-slate-700 dark:text-slate-200">
              {concentration.effective_n_holdings_capital.toFixed(1)}
            </strong>
          </div>
        </div>

        <div className={summaryCardClass}>
          <div className="text-[11px] text-slate-500 dark:text-slate-400">Volatility Risk HHI</div>
          <div className="mt-1 font-mono text-xl font-bold text-amber-600 dark:text-amber-400">
            {concentration.hhi_risk.toFixed(3)}
          </div>
          <div className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
            Eff. Risk N:{" "}
            <strong className="font-mono text-amber-600 dark:text-amber-300">
              {concentration.effective_n_holdings_risk.toFixed(1)}
            </strong>
          </div>
        </div>

        <div className={summaryCardClass}>
          <div className="text-[11px] text-slate-500 dark:text-slate-400">95% Tail CVaR HHI</div>
          <div className="mt-1 font-mono text-xl font-bold text-rose-600 dark:text-rose-400">
            {concentration.hhi_cvar_risk.toFixed(3)}
          </div>
          <div className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">Tail concentration</div>
        </div>

        <div className={summaryCardClass}>
          <div className="text-[11px] text-slate-500 dark:text-slate-400">Top-3 Capital Share</div>
          <div className="mt-1 font-mono text-xl font-bold text-slate-900 dark:text-white">
            {(concentration.top_3_capital_share * 100).toFixed(1)}%
          </div>
          <div className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">3 largest positions</div>
        </div>

        <div className={summaryCardClass}>
          <div className="text-[11px] text-slate-500 dark:text-slate-400">Top-3 Risk Share</div>
          <div className="mt-1 font-mono text-xl font-bold text-amber-600 dark:text-amber-400">
            {(concentration.top_3_risk_share * 100).toFixed(1)}%
          </div>
          <div className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">3 largest risk drivers</div>
        </div>

        <div className={summaryCardClass}>
          <div className="text-[11px] text-slate-500 dark:text-slate-400">Risk Gini Coefficient</div>
          <div className="mt-1 font-mono text-xl font-bold text-indigo-600 dark:text-indigo-400">
            {concentration.gini_risk.toFixed(3)}
          </div>
          <div className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
            Capital Gini:{" "}
            <strong className="font-mono text-slate-700 dark:text-slate-200">
              {concentration.gini_capital.toFixed(3)}
            </strong>
          </div>
        </div>
      </div>

      {/* Charts Row: Lorenz Inequality Curve + PCA Eigen-Spectrum */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Lorenz Risk Inequality Curve */}
        <div className={chartCardClass}>
          <div>
            <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-800 dark:text-white">
              <Scale className="h-4 w-4 text-indigo-500 dark:text-indigo-400" />
              Lorenz Capital &amp; Risk Concentration Curve
            </h3>
            <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
              Bowing further below the diagonal 45° line indicates higher risk inequality across holdings.
            </p>
          </div>

          <div className="mt-4 h-72 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={lorenzChartData} margin={{ top: 10, right: 16, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID} strokeOpacity={0.4} />
                <XAxis dataKey="holdingsPct" unit="%" stroke={CHART_AXIS} tick={{ fontSize: 11, fill: CHART_AXIS }} />
                <YAxis unit="%" domain={[0, 100]} stroke={CHART_AXIS} tick={{ fontSize: 11, fill: CHART_AXIS }} />
                <Tooltip
                  contentStyle={TOOLTIP_STYLE}
                  formatter={(value: number) => `${value.toFixed(1)}%`}
                  labelFormatter={(label) => `Cumulative Holdings: ${label}%`}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line
                  type="linear"
                  dataKey="equalityPct"
                  name="Equal Risk Parity (45°)"
                  stroke="#64748b"
                  strokeDasharray="5 5"
                  dot={false}
                  strokeWidth={1.5}
                />
                <Area
                  type="monotone"
                  dataKey="capitalPct"
                  name="Cumulative Capital"
                  stroke="#6366f1"
                  fill="#6366f1"
                  fillOpacity={0.15}
                  strokeWidth={2}
                />
                <Area
                  type="monotone"
                  dataKey="riskPct"
                  name="Cumulative Risk (PCTR)"
                  stroke="#f59e0b"
                  fill="#f59e0b"
                  fillOpacity={0.2}
                  strokeWidth={2.5}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* PCA Eigen-Spectrum & Portfolio Variance Share */}
        <div className={chartCardClass}>
          <div className="flex items-start justify-between gap-2">
            <div>
              <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-800 dark:text-white">
                <BarChart3 className="h-4 w-4 text-sky-500 dark:text-sky-400" />
                Principal Component Spectrum &amp; Meucci ENB Share
              </h3>
              <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                Compares asset-universe eigenvalue share against your portfolio's variance loading per PC.
              </p>
            </div>
            <span className="shrink-0 rounded-lg bg-indigo-500/10 px-2.5 py-1 font-mono text-xs font-semibold text-indigo-700 ring-1 ring-indigo-500/30 dark:text-indigo-300">
              ENB = {concentration.effective_number_of_bets.toFixed(2)}
            </span>
          </div>

          <div className="mt-4 h-72 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={pcaChartData} margin={{ top: 10, right: 16, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID} strokeOpacity={0.4} />
                <XAxis dataKey="component" stroke={CHART_AXIS} tick={{ fontSize: 11, fill: CHART_AXIS }} />
                <YAxis unit="%" domain={[0, 100]} stroke={CHART_AXIS} tick={{ fontSize: 11, fill: CHART_AXIS }} />
                <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(value: number) => `${value.toFixed(1)}%`} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="marketVariancePct" name="Universe Eigen Share" fill="#38bdf8" radius={[4, 4, 0, 0]} />
                <Bar dataKey="portfolioSharePct" name="Portfolio Variance Share" fill="#818cf8" radius={[4, 4, 0, 0]} />
                <Line type="monotone" dataKey="cumulativePct" name="Cumulative Explained" stroke="#34d399" strokeWidth={2} dot={{ r: 3 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Institutional TanStack Table: Euler Volatility & CVaR Risk Attribution */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900/90">
        <div className="flex flex-col gap-4 border-b border-slate-200 pb-4 dark:border-slate-800 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h3 className="flex items-center gap-2 text-base font-semibold text-slate-800 dark:text-white">
              <PieChart className="h-4 w-4 text-indigo-500 dark:text-indigo-400" />
              Asset-Level Euler Volatility &amp; 95% CVaR Risk Attribution
            </h3>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              Filter by HRP cluster or risk-overweight gap and click any column header to sort.
            </p>
          </div>

          {/* Search & Quick Filters */}
          <div className="flex flex-wrap items-center gap-2.5">
            {/* Search input */}
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
              <input
                type="text"
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
                placeholder="Filter ticker..."
                className="w-36 rounded-lg border border-slate-300 bg-white py-1.5 pl-8 pr-3 text-xs text-slate-900 placeholder-slate-400 focus:border-indigo-500 focus:outline-none dark:border-slate-700 dark:bg-slate-950 dark:text-white dark:placeholder-slate-500"
              />
            </div>

            {/* Risk Regime Filter */}
            <div className="inline-flex rounded-lg border border-slate-200 bg-slate-100 p-0.5 text-xs dark:border-slate-800 dark:bg-slate-950">
              {(
                [
                  { id: "all", label: "All Assets" },
                  { id: "overweight", label: "Risk Overweight" },
                  { id: "hedge", label: "Stabilizers / Hedges" },
                ] as const
              ).map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setRiskFilter(item.id)}
                  className={`rounded-md px-2.5 py-1 text-[11px] font-medium transition ${
                    riskFilter === item.id
                      ? "bg-indigo-600 text-white"
                      : "text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200"
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>

            {/* Cluster Filter Pills */}
            <div className="flex items-center gap-1">
              <Filter className="mr-1 h-3.5 w-3.5 text-slate-400 dark:text-slate-500" />
              <button
                type="button"
                onClick={() => setSelectedCluster("all")}
                className={`rounded-lg px-2 py-1 font-mono text-[11px] transition ${
                  selectedCluster === "all"
                    ? "bg-slate-700 text-white dark:bg-slate-700"
                    : "bg-slate-100 text-slate-500 hover:text-slate-800 dark:bg-slate-950 dark:text-slate-400 dark:hover:text-slate-200"
                }`}
              >
                All
              </button>
              {clusterIds.map((cId) => (
                <button
                  key={cId}
                  type="button"
                  onClick={() => setSelectedCluster(cId)}
                  className={`rounded-lg px-2 py-1 font-mono text-[11px] transition ${
                    selectedCluster === cId
                      ? "bg-indigo-600 text-white"
                      : "bg-slate-100 text-slate-500 hover:text-slate-800 dark:bg-slate-950 dark:text-slate-400 dark:hover:text-slate-200"
                  }`}
                >
                  C{cId}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Table Body */}
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="border-b border-slate-200 bg-slate-50/70 text-[11px] font-semibold uppercase tracking-wider text-slate-500 dark:border-slate-800 dark:bg-slate-950/70 dark:text-slate-400">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    const sorted = header.column.getIsSorted();
                    return (
                      <th
                        key={header.id}
                        onClick={header.column.getToggleSortingHandler()}
                        className="cursor-pointer select-none px-3 py-3 transition hover:text-slate-800 dark:hover:text-white"
                      >
                        <div className="flex items-center gap-1.5">
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {sorted === "asc" ? (
                            <ArrowUp className="h-3.5 w-3.5 text-indigo-500 dark:text-indigo-400" />
                          ) : sorted === "desc" ? (
                            <ArrowDown className="h-3.5 w-3.5 text-indigo-500 dark:text-indigo-400" />
                          ) : (
                            <ArrowUpDown className="h-3.5 w-3.5 text-slate-300 dark:text-slate-600" />
                          )}
                        </div>
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>
            <tbody className="divide-y divide-slate-200/70 dark:divide-slate-800/60">
              {table.getRowModel().rows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="py-8 text-center text-slate-400 dark:text-slate-500">
                    No holdings match the active filter criteria.
                  </td>
                </tr>
              ) : (
                table.getRowModel().rows.map((row) => (
                  <tr key={row.id} className="transition hover:bg-slate-100/60 dark:hover:bg-slate-800/40">
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className="px-3 py-3">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
